/**
 * Pure logic for what each computer can actually take right now.
 *
 * A computer that is connected is not the same as a computer that is being
 * given work. The engine holds a machine back when its newest reading says
 * less of it is free than the owner asked to keep, and it never puts more
 * workers on a computer than the places it has. Both of those look identical
 * on a list showing only a name, a dot and a last-seen time, so this module
 * turns the two views into one state a person can read: ready, full, held back
 * by a named reading, or not reported at all.
 *
 * Every number here comes from a row. A reading a computer did not take is
 * absent, and absent stays absent - nothing is averaged, defaulted or guessed.
 * No I/O and no clock: the moment arrives as `nowMs`.
 */
import { DISK_MIN_FREE_BYTES, DISK_MIN_FREE_PERCENT, diskFreeLabel } from '@brunel/shared';

/** One row of the capacity view: places, reserves, the newest reading, the verdict. */
export interface NodeCapacityLike {
  readonly node_id: string;
  readonly name: string;
  readonly worker_seats: number | null;
  readonly reserve_memory_percent: number | null;
  readonly reserve_graphics_percent: number | null;
  readonly observed_at: string | null;
  readonly memory_free_percent: number | null;
  readonly video_memory_free_percent: number | null;
  readonly active_runs: number | null;
  readonly accepting: boolean | null;
  readonly holding_back: string | null;
  /**
   * The two sustained ceilings and what they last measured (#483). Optional so an older
   * caller still type-checks; absent means this row cannot say, which is not the same as
   * a ceiling of zero and holds nothing back.
   */
  readonly cpu_ceiling_percent?: number | null;
  readonly cpu_sustained_percent?: number | null;
  readonly graphics_ceiling_percent?: number | null;
  readonly graphics_sustained_percent?: number | null;
}

/** One row of the pressure view: the newest sample a computer left behind. */
export interface NodePressureLike {
  readonly node_id: string;
  readonly observed_at: string | null;
  readonly cpu_count: number | null;
  readonly cpu_load_percent: number | null;
  readonly memory_free_percent: number | null;
  readonly video_memory_free_percent: number | null;
  readonly active_runs: number | null;
  /**
   * The volume that holds this computer's run checkouts. Optional so an older caller still
   * type-checks; absent or null means the computer has not said, which holds nothing back.
   */
  readonly disk_free_bytes?: number | null;
  readonly disk_total_bytes?: number | null;
  readonly disk_low?: boolean | null;
}

/** What is true of a computer right now, in the four ways that matter. */
export type Readiness = 'ready' | 'at_limit' | 'held_back' | 'not_reported';

/**
 * Which reading is holding a computer back. The view names the two reserves, since #483
 * the two sustained load ceilings, and the disk guard: a computer with under 15 GB or under
 * 10% free on the volume that holds its run checkouts takes no new work.
 */
export type HeldBackBy = 'memory' | 'video_memory' | 'cpu' | 'graphics' | 'disk';

/** How the line should read; these are the tone names the screen already uses. */
export type CapacityTone = 'success' | 'attention' | 'neutral';

/** A computer's state, with every figure it was possible to work out. */
export interface CapacityState {
  readonly nodeId: string;
  readonly readiness: Readiness;
  readonly heldBackBy: HeldBackBy | null;
  readonly tone: CapacityTone;
  /** Two or three words, to sit beside the name. */
  readonly phrase: string;
  /** The same thing said in full, for anyone who wants the why. */
  readonly line: string;
  /** The named reading and its figures, when something is holding the computer back. */
  readonly reason: string | null;
  /** Places for a worker still free, or null when the places or the runs are unknown. */
  readonly placesFree: number | null;
  /** How far free memory sits above the reserve, in points; null when either is absent. */
  readonly memoryHeadroomPercent: number | null;
  /** The same for video memory; null on a computer that cannot see its graphics. */
  readonly graphicsHeadroomPercent: number | null;
  /** How old the reading is, or null when there is no reading to age. */
  readonly readingAgeMs: number | null;
  /** Whether that reading is old enough that it should be said out loud. */
  readonly stale: boolean;
  /**
   * What is free on the volume that holds this computer's run checkouts, as it last
   * reported: "31.4 GB free of 930.5 GB (3.4%)". Null when it has not said.
   */
  readonly diskFree: string | null;
  /** That reading is under the disk guard, so the computer takes no new work. */
  readonly diskLow: boolean;
}

/** A reading older than this is still what the engine uses, but it is called old. */
export const READING_STALE_AFTER_MS = 300_000;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * How many places for a worker are still free. Null rather than zero when the
 * place count or the run count is missing: an unknown denominator is not a
 * full computer, and saying "no room" on a guess would be the whole bug again.
 */
export function placesFree(row: NodeCapacityLike): number | null {
  const places = row.worker_seats;
  const runs = row.active_runs;
  if (places === null || runs === null) return null;
  if (!Number.isFinite(places) || !Number.isFinite(runs) || places < 0) return null;
  return Math.max(0, places - runs);
}

/** Free minus reserve, in percentage points. Null when either figure is absent. */
export function headroomPercent(freePercent: number | null, reservePercent: number | null): number | null {
  if (freePercent === null || reservePercent === null) return null;
  if (!Number.isFinite(freePercent) || !Number.isFinite(reservePercent)) return null;
  return freePercent - reservePercent;
}

/** How long ago the reading was taken, or null when there is no readable time. */
export function readingAgeMs(observedAt: string | null, nowMs: number): number | null {
  if (observedAt === null) return null;
  const takenMs = Date.parse(observedAt);
  if (Number.isNaN(takenMs)) return null;
  return nowMs - takenMs;
}

/** "under a minute old", "4m old", "3h old", "2d old" - only about a reading that exists. */
export function readingAgeLabel(ageMs: number | null): string | null {
  if (ageMs === null) return null;
  if (ageMs < MINUTE_MS) return 'under a minute old';
  if (ageMs < HOUR_MS) return `${Math.floor(ageMs / MINUTE_MS)}m old`;
  if (ageMs < DAY_MS) return `${Math.floor(ageMs / HOUR_MS)}h old`;
  return `${Math.floor(ageMs / DAY_MS)}d old`;
}

/** The reading the view named, in the owner's words rather than a column name. */
export function readingName(kind: string): string {
  if (kind === 'memory') return 'free memory';
  if (kind === 'video_memory') return 'free video memory';
  if (kind === 'cpu') return 'sustained processor load';
  if (kind === 'graphics') return 'sustained graphics load';
  if (kind === 'disk') return 'free disk space';
  return kind.replaceAll('_', ' ');
}

/** Only the kinds the view can name count as a named reading. */
export function heldBackBy(value: string | null): HeldBackBy | null {
  return value === 'memory' || value === 'video_memory' || value === 'cpu' || value === 'graphics' || value === 'disk'
    ? value : null;
}

function reserveFor(row: NodeCapacityLike, kind: HeldBackBy): number | null {
  return kind === 'memory' ? row.reserve_memory_percent : row.reserve_graphics_percent;
}

function freeFor(row: NodeCapacityLike, kind: HeldBackBy): number | null {
  return kind === 'memory' ? row.memory_free_percent : row.video_memory_free_percent;
}

/** What the disk guard keeps free, in the owner's words. */
const DISK_GUARD_FLOOR = `${DISK_MIN_FREE_BYTES / (1024 ** 3)} GB and ${DISK_MIN_FREE_PERCENT}%`;

/** What the newest sample says is free on the checkout volume, or null when it did not say. */
export function diskFreeText(pressure: NodePressureLike | null): string | null {
  const free = pressure?.disk_free_bytes ?? null;
  const total = pressure?.disk_total_bytes ?? null;
  if (free === null || total === null || !Number.isFinite(free) || !Number.isFinite(total) || total <= 0) return null;
  return diskFreeLabel({ freeBytes: free, totalBytes: total });
}

/** The whole reason for a computer that is being held back, figures and all. */
function reasonFor(row: NodeCapacityLike, kind: HeldBackBy, pressure: NodePressureLike | null = null): string {
  const name = readingName(kind);
  // The disk guard is Brunel's own floor, not a reserve the owner set: said as that.
  if (kind === 'disk') {
    const free = diskFreeText(pressure);
    return free === null
      ? `${name} is under the ${DISK_GUARD_FLOOR} Brunel keeps free`
      : `${name} is ${free}, under the ${DISK_GUARD_FLOOR} Brunel keeps free`;
  }
  // A sustained ceiling is the other way round from a reserve: too much in use rather than
  // too little left. Both say the same thing to the owner - it is over what you allowed.
  if (kind === 'cpu' || kind === 'graphics') {
    const sustained = (kind === 'cpu' ? row.cpu_sustained_percent : row.graphics_sustained_percent) ?? null;
    const ceiling = (kind === 'cpu' ? row.cpu_ceiling_percent : row.graphics_ceiling_percent) ?? null;
    if (sustained === null || ceiling === null) return `${name} is over the ceiling set for this computer`;
    return `${name} has stayed at ${sustained}%, at or above the ${ceiling}% ceiling set for this computer`;
  }
  const free = freeFor(row, kind);
  const reserve = reserveFor(row, kind);
  if (free === null || reserve === null) return `${name} is under the share this computer keeps free`;
  return `${name} is ${free}%, under the ${reserve}% this computer keeps free`;
}

function placesPhrase(free: number | null, places: number | null): string | null {
  if (free === null || places === null) return null;
  return `${free} of ${places} ${places === 1 ? 'place' : 'places'} free`;
}

const NOT_REPORTED = {
  readiness: 'not_reported',
  heldBackBy: null,
  tone: 'neutral',
  phrase: 'not reported',
  line: 'This computer has not reported what it is under, so nothing is known about its room.',
  reason: null,
} as const;

/**
 * One computer's state, from its capacity row and its newest sample. A
 * computer with no capacity row at all is not reported; so is one that has
 * never been measured and still has a place free. Being full is said before
 * being held back, because a full computer is given no further worker whatever
 * its readings say.
 */
export function capacityState(
  capacity: NodeCapacityLike | null,
  pressure: NodePressureLike | null,
  nowMs: number,
  nodeId: string | null = null,
): CapacityState {
  const id = capacity?.node_id ?? nodeId ?? '';
  const blank = {
    nodeId: id,
    placesFree: null,
    memoryHeadroomPercent: null,
    graphicsHeadroomPercent: null,
    readingAgeMs: null,
    stale: false,
    // The disk reading is the newest sample's own, whatever else is or is not known.
    diskFree: diskFreeText(pressure),
    diskLow: pressure?.disk_low === true,
  } as const;
  if (capacity === null) return { ...blank, ...NOT_REPORTED };

  const free = placesFree(capacity);
  const places = capacity.worker_seats;
  const observedAt = capacity.observed_at ?? pressure?.observed_at ?? null;
  const ageMs = readingAgeMs(observedAt, nowMs);
  const stale = ageMs !== null && ageMs > READING_STALE_AFTER_MS;
  const ageLabel = stale ? readingAgeLabel(ageMs) : null;
  const base = {
    nodeId: id,
    placesFree: free,
    memoryHeadroomPercent: headroomPercent(capacity.memory_free_percent, capacity.reserve_memory_percent),
    graphicsHeadroomPercent: headroomPercent(capacity.video_memory_free_percent, capacity.reserve_graphics_percent),
    readingAgeMs: ageMs,
    stale,
    diskFree: blank.diskFree,
    diskLow: blank.diskLow,
  } as const;
  const olderThan = ageLabel === null ? '' : ` The reading behind this is ${ageLabel}.`;

  if (free === 0) {
    if (places === 0) {
      return {
        ...base,
        readiness: 'at_limit',
        heldBackBy: null,
        tone: 'attention',
        phrase: 'viewer/control only',
        line: 'This computer has zero Worker seats and cannot take new work.',
        reason: null,
      };
    }
    const phrase = placesPhrase(free, places);
    return {
      ...base,
      readiness: 'at_limit',
      heldBackBy: null,
      tone: 'attention',
      phrase: 'at its limit',
      line: phrase === null
        ? 'Busy: every place on this computer is taken. New work waits for one to free up.'
        : `Busy: ${phrase}. New work waits for a place to free up.`,
      reason: null,
    };
  }

  const kind = heldBackBy(capacity.holding_back);
  if (kind !== null) {
    const reason = reasonFor(capacity, kind, pressure);
    return {
      ...base,
      readiness: 'held_back',
      heldBackBy: kind,
      tone: 'attention',
      phrase: 'held back',
      line: `Held back: ${reason}. New work goes elsewhere until a later reading says otherwise.${olderThan}`,
      reason,
    };
  }

  if (observedAt === null) return { ...base, ...NOT_REPORTED };

  const parts = [
    placesPhrase(free, places),
    base.memoryHeadroomPercent === null ? null : `${base.memoryHeadroomPercent} points of memory above its reserve`,
    pressure === null || pressure.cpu_load_percent === null ? null : `${pressure.cpu_load_percent}% of its processors in use`,
  ].filter((part): part is string => part !== null);
  return {
    ...base,
    readiness: 'ready',
    heldBackBy: null,
    tone: 'success',
    phrase: 'ready',
    line: parts.length === 0 ? `Ready for work.${olderThan}` : `Ready for work: ${parts.join(', ')}.${olderThan}`,
    reason: null,
  };
}

/** The rows of a view, by the computer they belong to; the first row per computer wins. */
export function byNode<T extends { readonly node_id: string }>(rows: readonly T[]): Map<string, T> {
  const out = new Map<string, T>();
  for (const row of rows) if (!out.has(row.node_id)) out.set(row.node_id, row);
  return out;
}

/** The state of one computer, looked up in both views at once. */
export function stateForNode(
  nodeId: string,
  capacity: readonly NodeCapacityLike[],
  pressure: readonly NodePressureLike[],
  nowMs: number,
): CapacityState {
  return capacityState(byNode(capacity).get(nodeId) ?? null, byNode(pressure).get(nodeId) ?? null, nowMs, nodeId);
}

/** The lowest and highest reserve the guarded write accepts, as the database states them. */
export const RESERVE_MIN_PERCENT = 0;
export const RESERVE_MAX_PERCENT = 90;

/** What the owner typed into the two reserve boxes; an empty box keeps what there is. */
export interface LimitsInput {
  readonly memory: string;
  readonly graphics: string;
}

/** Exactly the keys the guarded write accepts beside the computer's own identifiers. */
export interface LimitsFields {
  readonly reserveMemoryPercent?: number;
  readonly reserveGraphicsPercent?: number;
}

export type LimitsCheck =
  | { readonly ok: true; readonly value: LimitsFields }
  | { readonly ok: false; readonly message: string };

function percent(text: string, label: string): { ok: true; value: number } | { ok: false; message: string } {
  if (!/^[0-9]{1,2}$/.test(text)) {
    return { ok: false, message: `${label} must be a whole number of percent between ${RESERVE_MIN_PERCENT} and ${RESERVE_MAX_PERCENT}.` };
  }
  const value = Number(text);
  if (value < RESERVE_MIN_PERCENT || value > RESERVE_MAX_PERCENT) {
    return { ok: false, message: `${label} must be between ${RESERVE_MIN_PERCENT} and ${RESERVE_MAX_PERCENT} percent.` };
  }
  return { ok: true, value };
}

/**
 * What to send, or why nothing can be. A box left empty keeps what the computer
 * already has, so two empty boxes are nothing to say rather than a silent
 * no-op, and no reserve outside the range the database accepts ever reaches it.
 */
export function checkLimits(input: LimitsInput): LimitsCheck {
  const memory = input.memory.trim();
  const graphics = input.graphics.trim();
  if (memory === '' && graphics === '') {
    return { ok: false, message: 'Enter a memory reserve, a video memory reserve, or both.' };
  }
  let value: LimitsFields = {};
  if (memory !== '') {
    const parsed = percent(memory, 'The memory reserve');
    if (!parsed.ok) return { ok: false, message: parsed.message };
    value = { ...value, reserveMemoryPercent: parsed.value };
  }
  if (graphics !== '') {
    const parsed = percent(graphics, 'The video memory reserve');
    if (!parsed.ok) return { ok: false, message: parsed.message };
    value = { ...value, reserveGraphicsPercent: parsed.value };
  }
  return { ok: true, value };
}
